import { z } from "zod";

import { SECURITY_HISTORY_FAMILIES } from "./securityHistory";

/**
 * HESAP/KİMLİK/OTURUM AI RİSK SİNYALLERİ (CLOS-287 `[191]` · CLOS-1009/1010).
 *
 * ### ⚠ BU BİR GÜVENLİK KARARI DEĞİL, BİR OKUMA YARDIMI
 *
 * Kartın kapsam sınırı tek cümlede: *"AI kimlik doğrulama, rate-limit, hesap kilidi,
 * oturum iptali, 2FA, hesap durumu veya güvenlik yaptırımı uygulamaz."* Bu yüzden şemada
 * bir "karar", "eylem" ya da "yaptırım" alanı YOK — yalnız `suggestedStep`, ve o da
 * kullanıcının kendi eliyle gideceği kanonik akışa işaret eden bir CÜMLE.
 *
 * ### ⚠ SİNYAL KAYNAK OLAY ARALIĞINA BAĞLI
 *
 * Kart: *"Her risk sinyali kaynak olay/correlation aralığı, gözlenen örüntü, güven/
 * belirsizlik düzeyi ve önerilen insan inceleme adımıyla sunulur."* `familyler`,
 * `eventCount` ve `windowStart/End` bunun karşılığı: kullanıcı sinyalin hangi olaylardan
 * çıktığını kendi geçmişinde bulabilmeli. Kaynağı olmayan bir sinyal, doğrulanamaz bir
 * iddiadır.
 */

/**
 * ⚠ ÖNCELİK, "TEHLİKE DERECESİ" DEĞİL. Adlar bilinçli olarak eylem değil İNCELEME dili:
 * *"Yanlış pozitif kesin suçlama, dolandırıcılık kararı veya kullanıcıya gösterilen
 * tartışmasız gerçek gibi sunulmaz."*
 */
export const AI_SECURITY_SIGNAL_PRIORITIES = ["high", "medium", "low"] as const;
export type AiSecuritySignalPriority = (typeof AI_SECURITY_SIGNAL_PRIORITIES)[number];

export const AI_SECURITY_SIGNAL_PRIORITY_NAMES: Record<AiSecuritySignalPriority, string> = {
  high: "Önce bunu inceleyin",
  medium: "İncelenmeli",
  low: "Bilgi amaçlı",
};

export const AI_SECURITY_SIGNAL_REVIEW_STATES = ["open", "reviewed", "dismissed"] as const;
export type AiSecuritySignalReviewState = (typeof AI_SECURITY_SIGNAL_REVIEW_STATES)[number];

export const AI_SECURITY_SIGNAL_REVIEW_STATE_NAMES: Record<AiSecuritySignalReviewState, string> = {
  open: "İncelenmedi",
  reviewed: "İncelendi",
  dismissed: "Yok sayıldı",
};

/**
 * ⚠ EKRANDA ZORUNLU CÜMLE. Güvenlik alanında yanlış pozitifin bedeli yüksek: kullanıcı
 * kendi hesabını "ele geçirilmiş" sanıp paniğe kapılabilir. Metin şemada, çünkü ekranın
 * kendi cümlesini yazması bu uyarının bir gün yumuşamasına açık kapı bırakırdı.
 */
export const AI_SECURITY_SIGNAL_ADVISORY =
  "Bu sinyaller bir güvenlik kararı, ihlal tespiti ya da suçlama değildir; hesabınızda " +
  "neye bakmanız gerektiğini göstermek için sıralanmıştır.";

export const AI_SECURITY_SIGNAL_EMPTY_MESSAGE =
  "Bu dönemde dikkat çeken bir örüntü çıkmadı. Bu, hesabınızda hiçbir risk olmadığı " +
  "anlamına gelmez.";

export const aiSecuritySignalSchema = z.strictObject({
  /** Kısa başlık — kullanıcı listede bunu okuyor. */
  title: z.string().min(1).max(200),
  priority: z.enum(AI_SECURITY_SIGNAL_PRIORITIES),
  confidence: z.enum(["high", "medium", "low"]),
  /**
   * ⚠ HANGİ OLAY AİLELERİNDEN çıktığı — ham olay adı DEĞİL. `securityHistory.ts`'in aynı
   * kararı: ham ad kullanıcıya iç kataloğumuzu okutur ve modele de gerekmiyor.
   */
  families: z.array(z.enum(SECURITY_HISTORY_FAMILIES)).min(1).max(9),
  /** Sinyalin dayandığı olay SAYISI — hangi olaylar olduğu değil (kaynak zaten kullanıcıda). */
  eventCount: z.number().int().min(1).max(10_000),
  /** Gözlem penceresi; kullanıcı kendi geçmişinde o aralığa bakabilsin diye. */
  windowStart: z.string().min(1).max(40),
  windowEnd: z.string().min(1).max(40),
  /** Gözlenen örüntünün açıklaması — kesin hüküm değil, gözlem. */
  pattern: z.string().min(1).max(1000),
  /** ⚠ ÖNERİLEN İNSAN ADIMI: bir komut değil, kullanıcının kendi eliyle yapacağı iş. */
  suggestedStep: z.string().min(1).max(500),
});
export type AiSecuritySignal = z.infer<typeof aiSecuritySignalSchema>;

/**
 * ⚠ `reviewNeeded` BULGU DEĞİL: kanıt yetersizse model sinyal UYDURMAK yerine neye
 * bakılması gerektiğini yazıyor (CLOS-206'daki aynı ayrım ve aynı gerekçe).
 */
export const aiSecuritySignalOutputSchema = z.strictObject({
  signals: z.array(aiSecuritySignalSchema).max(20),
  reviewNeeded: z.array(z.string().min(1).max(300)).max(10).default([]),
});
export type AiSecuritySignalOutput = z.infer<typeof aiSecuritySignalOutputSchema>;

export const AI_SECURITY_SIGNAL_ERROR_CODES = ["provider_error", "invalid_output"] as const;
export type AiSecuritySignalErrorCode = (typeof AI_SECURITY_SIGNAL_ERROR_CODES)[number];

export const AI_SECURITY_SIGNAL_ERROR_MESSAGES: Record<AiSecuritySignalErrorCode, string> = {
  provider_error: "AI sağlayıcısına ulaşılamadı; sinyal üretilemedi ve krediniz iade edildi.",
  invalid_output: "AI beklenen biçimde bir sonuç üretmedi; kaydedilmedi ve krediniz iade edildi.",
};

export const AI_SECURITY_SIGNAL_NO_HISTORY_MESSAGE =
  "Hesabınızda henüz incelenecek bir güvenlik olayı yok.";

/**
 * SİNYAL DURUMU GÜNCELLEME.
 *
 * ⚠ İNDEKSLE: sinyaller bir ÇALIŞMANIN çıktısındaki sıradan ibaret; her sinyale ayrı satır
 * açmak, hiç bakılmayan çalışmalar için de satır yazmak olurdu (CLOS-206'da bu yüzden
 * bulgular yalnız kaydetmede satırlaşıyor; burada kaydetme adımı YOK — güvenlik
 * geçmişinin kendisi zaten kalıcı).
 */
export const updateAiSecuritySignalSchema = z.strictObject({
  index: z.coerce.number().int().min(0).max(19),
  reviewState: z.enum(AI_SECURITY_SIGNAL_REVIEW_STATES),
});
export type UpdateAiSecuritySignalInput = z.infer<typeof updateAiSecuritySignalSchema>;

export type AiSecuritySignalRunSummary = {
  id: string;
  status: "completed" | "failed";
  output: AiSecuritySignalOutput | null;
  /** ⚠ Sinyal indeksine göre inceleme durumu; `open` olanlar burada YOK. */
  reviewStates: Record<string, AiSecuritySignalReviewState>;
  /**
   * ⚠ KESİN SUÇLAMA DİLİ KULLANAN SİNYALLERİN İNDEKSLERİ (CLOS-1086).
   *
   * Kart: *"kullanıcıyı suç/fraud/ihlalle kesin biçimde etiketlemez."* Sinyal
   * SİLİNMİYOR — işaretleniyor; risk ailesinin `certaintyFlagged` kararıyla aynı gerekçe.
   *
   * ⚠ BİÇİM DİĞER AİLELERDEN FARKLI ve bilinçli: bu aile ham `output`'u döndürüyor
   * (sinyal başına bir görünüm tipi yok, `reviewStates` de indeksle çalışıyor). Sinyal
   * başına bir alan eklemek `output`'u modelin ürettiği hâlden uzaklaştırırdı; indeks
   * listesi `reviewStates` ile AYNI adresleme biçimini kullanıyor.
   */
  certaintyFlaggedIndexes: number[];
  errorCode: AiSecuritySignalErrorCode | null;
  model: string;
  promptVersion: number;
  schemaVersion: number;
  credits: number;
  normalizedPages: number;
  coefficientVersion: number;
  /** Kaç güvenlik olayı özetlendi — kullanıcı kapsamı bilsin diye. */
  eventCount: number;
  createdAt: string;
};
